import { useVirtualizer } from '@tanstack/react-virtual';
import { FC, useMemo, useRef, useState } from 'react';

import { selectSellers } from '@savgroup-front-common/core/domains/sellers/selectors';
import {
  removeAccent,
  safeFormattedIntlString,
} from '@savgroup-front-common/core/formatters';
import { useIsSuperAdmin } from '@savgroup-front-common/core/hooks';
import { CopyButton } from '@savgroup-front-common/primitive/components/copy-button';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import {
  Item,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from '@savgroup-front-common/primitive/ui/item';
import {
  RadioGroup,
  RadioGroupItem,
} from '@savgroup-front-common/primitive/ui/radio-group';

import { useControlTypedSelector } from '@/hooks';
import { useAppContext } from '@/view/app/App.context';

import { ClearableSearchInputGroup } from '../../ClearableSearchInputGroup/ClearableSearchInputGroup';

import messages from './messages';

const SELLER_ITEM_HEIGHT = 56;
const SUPER_ADMIN_SELLER_ITEM_HEIGHT = 84;
const SELLER_LIST_OVERSCAN = 8;

interface SelectSellerControlProps {
  onClose: () => void;
}

export const SelectSellerControl: FC<SelectSellerControlProps> = ({
  onClose,
}) => {
  const { selectedSeller, onSelectSeller } = useAppContext();
  const sellers = useControlTypedSelector(selectSellers);
  const isSuperAdmin = useIsSuperAdmin();

  const [search, setSearch] = useState<string>('');

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const visibleSellers = useMemo(() => {
    const query = removeAccent(search.trim().toLowerCase());

    const base = query
      ? sellers.filter(
          (seller) =>
            removeAccent((seller.name || '').toLowerCase()).includes(query) ||
            removeAccent((seller.id || '').toLowerCase()).includes(query) ||
            removeAccent((seller.tenantId || '').toLowerCase()).includes(query),
        )
      : sellers;

    return [...base].sort((a, b) => {
      if (a.id === selectedSeller?.id) {
        return -1;
      }
      if (b.id === selectedSeller?.id) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });
  }, [search, sellers, selectedSeller?.id]);

  const rowVirtualizer = useVirtualizer({
    count: visibleSellers.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () =>
      isSuperAdmin ? SUPER_ADMIN_SELLER_ITEM_HEIGHT : SELLER_ITEM_HEIGHT,
    overscan: SELLER_LIST_OVERSCAN,
  });

  return (
    <>
      <ClearableSearchInputGroup
        value={search}
        onChange={setSearch}
        placeholder={safeFormattedIntlString(messages.searchPlaceholder)}
        clearAriaLabel={safeFormattedIntlString(messages.clearSearch)}
        inputDataTestId="searchInput_input"
        clearDataTestId="searchInput_clear"
        autoFocus
      />

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <RadioGroup
          value={selectedSeller?.id}
          onValueChange={(sellerId) => {
            onSelectSeller(sellerId);
            onClose();
          }}
          className="contents"
        >
          <div
            style={{ height: rowVirtualizer.getTotalSize() }}
            className="relative w-full"
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const seller = visibleSellers[virtualItem.index];

              if (!seller) {
                return null;
              }

              const radioId = `selectSellerControl_radio_${seller.id}`;

              return (
                <div
                  key={virtualItem.index}
                  style={{ transform: `translateY(${virtualItem.start}px)` }}
                  className="absolute top-0 left-0 w-full pb-1"
                >
                  <Item
                    asChild
                    variant="outline"
                    size="xs"
                    className={cn(
                      'cursor-pointer hover:border-border-base-hover hover:bg-surface-base-hover',
                      'has-data-checked:border-border-primary-default has-data-checked:bg-surface-base-active',
                    )}
                  >
                    <label htmlFor={radioId}>
                      <ItemMedia>
                        <RadioGroupItem
                          id={radioId}
                          value={String(seller.id)}
                        />
                      </ItemMedia>
                      <ItemContent className="min-w-0">
                        <ItemTitle className="w-full min-w-0 truncate">
                          {seller.name}
                        </ItemTitle>
                        {isSuperAdmin && (
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <CopyButton
                              value={seller.id}
                              label={safeFormattedIntlString(
                                messages.copySellerId,
                              )}
                            >
                              <span className="w-full min-w-0 truncate">
                                sellerId: {seller.id}
                              </span>
                            </CopyButton>
                            <CopyButton
                              value={seller.tenantId}
                              label={safeFormattedIntlString(
                                messages.copyTenantId,
                              )}
                            >
                              <span className="w-full min-w-0 truncate">
                                tenantId: {seller.tenantId}
                              </span>
                            </CopyButton>
                          </div>
                        )}
                      </ItemContent>
                    </label>
                  </Item>
                </div>
              );
            })}
          </div>
        </RadioGroup>
      </div>
    </>
  );
};
